import DataTable from 'datatables.net-bs5';
import * as Highcharts from 'highcharts';

import { registerSyncChart, initCrosshairSync } from './home/crosshair-sync';
import { colorForRoute } from './toolbox-benchmarks';
import { getChartTheme } from './utils/chartTheme';

// ── Payload types (mirror PerfModel::getRouteHistory shape — Plan 01 task 3) ───

interface MetricSeries {
    [route: string]: Array<[number, number]>;
}

interface RouteHistory {
    routes: string[];
    series: {
        ttfb_ms: MetricSeries;
        fcp_ms: MetricSeries;
        lcp_ms: MetricSeries;
        cls: MetricSeries;
        db_total_ms: MetricSeries;
        bundle_kb: MetricSeries;
        [key: string]: MetricSeries;
    };
    // ts_ms (number) → run_id (number). D-07: powers click-to-navigate.
    runIdByTs: Record<number, number>;
}

declare global {
    interface Window {
        route_history?: RouteHistory;
        route_slug?: string;
    }
}

type MetricKey = 'ttfb_ms' | 'fcp_ms' | 'lcp_ms' | 'cls' | 'db_total_ms' | 'bundle_kb';

interface MetricConfig {
    key: MetricKey;
    elId: string;
    title: string;
    yTitle: string;
    ySuffix: string;
    decimals: number;
}

const METRICS: MetricConfig[] = [
    { key: 'ttfb_ms',     elId: 'route-chart-ttfb',   title: 'TTFB',        yTitle: 'ms',    ySuffix: ' ms', decimals: 0 },
    { key: 'fcp_ms',      elId: 'route-chart-fcp',    title: 'FCP',         yTitle: 'ms',    ySuffix: ' ms', decimals: 0 },
    { key: 'lcp_ms',      elId: 'route-chart-lcp',    title: 'LCP',         yTitle: 'ms',    ySuffix: ' ms', decimals: 0 },
    { key: 'cls',         elId: 'route-chart-cls',    title: 'CLS',         yTitle: 'value', ySuffix: '',    decimals: 3 },
    { key: 'db_total_ms', elId: 'route-chart-db',     title: 'DB Total',    yTitle: 'ms',    ySuffix: ' ms', decimals: 0 },
    { key: 'bundle_kb',   elId: 'route-chart-bundle', title: 'Bundle Size', yTitle: 'KB',    ySuffix: ' KB', decimals: 1 },
];

// ── Highcharts init ─────────────────────────────────────────────────────────

function initCharts(data: RouteHistory, route: string): void {
    // D-07: same per-route hash color across all 6 charts — visual identity
    // consistent with the dashboard's multi-route trend charts (Phase 99 D-06).
    const color = colorForRoute(route);

    for (const m of METRICS) {
        const el = document.getElementById(m.elId);
        if (!el) continue;

        const seriesData = data.series[m.key]?.[route] ?? [];

        // Single-series array (this route only — Plan-truth: 6 charts × 1 series each)
        const series: Highcharts.SeriesOptionsType[] = [{
            type: 'line',
            name: route,
            color,
            data: seriesData,
            marker: { enabled: true, radius: 4 },
            cursor: 'pointer',
            point: {
                events: {
                    // D-07: click any point → /toolbox/benchmarks?run=<id>
                    click: function (this: Highcharts.Point): void {
                        const runId = window.route_history?.runIdByTs?.[this.x as number];
                        if (runId) {
                            window.location.href = '/toolbox/benchmarks?run=' + runId;
                        }
                    },
                },
            },
        }];

        // Anti-pattern guard: never pass explicit undefined to Highcharts
        // (memory: feedback_highcharts_undefined_options) — use conditional spread
        // when an option might be absent. Every option below is set unconditionally.
        const chart = Highcharts.chart(el, Highcharts.merge(getChartTheme(), {
            chart: {
                type: 'line',
                height: 320,
                // CR WR-01 fix: events MUST be nested under `chart` — Highcharts has no
                // root-level `events` option, so a root-level handler is silently dropped
                // (the `as Highcharts.Options` cast masked the excess-property error).
                events: {
                    render: function (this: Highcharts.Chart) {
                        // D-04: re-apply crosshair-sync pointer.reset noop after any chart.update()
                        // (including responsive breakpoint crossing at 540px). Idempotency
                        // guard in registerSyncChart (syncCharts.includes + _crosshairSyncPatched)
                        // prevents duplicate registration on repeated render events.
                        registerSyncChart(this);
                    },
                },
            },
            title: {
                text: m.title,
                style: { fontSize: '14px' },
            },
            credits: { enabled: false },
            xAxis: {
                type: 'datetime',
                // REQUIRED — crosshair-sync.ts:89,134 calls drawCrosshair() which
                // only renders when xAxis.crosshair is configured. Pitfall 5.
                crosshair: { width: 1, color: '#999' },
            },
            yAxis: {
                title: { text: m.yTitle },
                gridLineWidth: 0.1,
            },
            tooltip: {
                xDateFormat: '%Y-%m-%d %H:%M',
                pointFormatter: function (this: Highcharts.Point): string {
                    const val = this.y !== null && this.y !== undefined
                        ? Number(this.y).toLocaleString(undefined, { maximumFractionDigits: m.decimals })
                        : '—';
                    return `<span style="color:${(this as Highcharts.Point & { color: string }).color}">●</span> ${this.series.name}: <b>${val}${m.ySuffix}</b><br/>`;
                },
            },
            legend: {
                enabled: false,
            },
            plotOptions: {
                line: {
                    animation: false,
                    marker: { enabled: true, radius: 4 },
                },
            },
            series,
        } as Highcharts.Options));

        // D-07: register every chart with the shared-crosshair pool.
        // initCrosshairSync() (called once after the loop) wires the .col-charts
        // container mousemove listener that drives all registered charts.
        registerSyncChart(chart);
    }
}

// ── Slowest-queries DataTable (D-08) ────────────────────────────────────────

function initSlowestQueriesTable(): void {
    const el = document.querySelector('table[data-table="slowest-queries"]') as HTMLTableElement | null;
    if (!el) return;

    new DataTable(el, {
        pageLength: 25,
        order: [[3, 'desc']],   // ms column DESC — slowest first (D-08)
    });
}

// ── Module export ───────────────────────────────────────────────────────────

export default class {
    static init = (): void => {
        // Picker / not_found / any-other-page → bail (Shared Pattern F)
        if (!window.route_history || !window.route_slug) return;

        initCharts(window.route_history, window.route_slug);
        initCrosshairSync();
        initSlowestQueriesTable();
    };
}
